import { useState, useMemo, useEffect, forwardRef } from 'react';
import { Holding } from '@/types/portfolio';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { ChevronDown, RefreshCw } from 'lucide-react';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { EmptyHoldings } from '@/components/ui/empty-states';
import { SampleAccount } from './LinkedAccountsPanel';

interface HoldingsTableProps {
  holdings: Holding[];
  onUpdate: (holdings: Holding[]) => void;
  sampleAccounts?: SampleAccount[];
  onNavigateToAccounts?: () => void;
}

interface AccountGroup {
  id: string;
  label: string;
  institution: string;
  taxLabel: string;
  accountMask: string;
  holdings: Holding[];
  totalValue: number;
}

// Map holding ID prefix to sample account ID
function getAccountIdForHolding(holdingId: string): string {
  if (holdingId.startsWith('br-')) return 'sample-brokerage';
  if (holdingId.startsWith('ira-')) return 'sample-trad-ira';
  if (holdingId.startsWith('roth-')) return 'sample-roth-ira';
  if (holdingId.startsWith('p-401k-')) return 'sample-partner-401k';
  if (holdingId.startsWith('p-roth-')) return 'sample-partner-roth';
  if (holdingId.startsWith('p-br-')) return 'sample-partner-brokerage';
  return 'unknown';
}

function groupHoldingsBySampleAccounts(holdings: Holding[], sampleAccounts: SampleAccount[]): AccountGroup[] {
  // Create a map of account ID to holdings
  const accountHoldings: Record<string, Holding[]> = {};

  holdings.forEach(h => {
    const accountId = getAccountIdForHolding(h.id);
    if (!accountHoldings[accountId]) {
      accountHoldings[accountId] = [];
    }
    accountHoldings[accountId].push(h);
  });

  // Build account groups in the order of sampleAccounts
  return sampleAccounts
    .filter(account => accountHoldings[account.id]?.length > 0)
    .map(account => ({
      id: account.id,
      label: account.account_name,
      institution: account.institution_name,
      taxLabel: account.account_type === 'Taxable' ? 'Taxable' : 'Tax-Advantaged',
      accountMask: account.account_mask,
      holdings: accountHoldings[account.id] || [],
      totalValue: (accountHoldings[account.id] || []).reduce((sum, h) => sum + h.shares * h.currentPrice, 0),
    }));
}

function groupHoldingsByInference(holdings: Holding[]): AccountGroup[] {
  const groups: Record<string, Holding[]> = {
    'brokerage': [],
    'traditional-ira': [],
    'roth-ira': [],
  };

  holdings.forEach(h => {
    // Simple inference based on holding name or account type
    const name = (h.name || '').toLowerCase();
    const id = (h.id || '').toLowerCase();

    if (name.includes('roth') || id.includes('roth')) {
      groups['roth-ira'].push(h);
    } else if (name.includes('ira') || name.includes('trad') || id.includes('ira')) {
      groups['traditional-ira'].push(h);
    } else {
      groups['brokerage'].push(h);
    }
  });

  const accountConfig: Record<string, { label: string; taxLabel: string }> = {
    'brokerage': { label: 'Brokerage Account', taxLabel: 'Taxable' },
    'traditional-ira': { label: 'Traditional IRA', taxLabel: 'Tax-Deferred' },
    'roth-ira': { label: 'Roth IRA', taxLabel: 'Tax-Free' },
  };

  const order = ['brokerage', 'traditional-ira', 'roth-ira'];

  return order
    .filter(key => groups[key].length > 0)
    .map(key => ({
      id: key,
      label: accountConfig[key].label,
      institution: '',
      taxLabel: accountConfig[key].taxLabel,
      accountMask: '',
      holdings: groups[key],
      totalValue: groups[key].reduce((sum, h) => sum + h.shares * h.currentPrice, 0),
    }));
}

interface AllocationBreakdown {
  stocksPct: number;
  bondsPct: number;
  cashPct: number;
  otherPct: number;
}

function computeAllocation(holdings: Holding[]): AllocationBreakdown {
  const totals = { stocks: 0, bonds: 0, cash: 0, other: 0 };

  holdings.forEach(h => {
    const value = h.shares * h.currentPrice;
    const assetClass = h.assetClass || 'Other';

    if (assetClass === 'US Stocks' || assetClass === 'Intl Stocks') {
      totals.stocks += value;
    } else if (assetClass === 'Bonds') {
      totals.bonds += value;
    } else if (assetClass === 'Cash') {
      totals.cash += value;
    } else {
      totals.other += value;
    }
  });

  const total = totals.stocks + totals.bonds + totals.cash + totals.other;

  if (total === 0) {
    return { stocksPct: 0, bondsPct: 0, cashPct: 0, otherPct: 0 };
  }

  return {
    stocksPct: (totals.stocks / total) * 100,
    bondsPct: (totals.bonds / total) * 100,
    cashPct: (totals.cash / total) * 100,
    otherPct: (totals.other / total) * 100,
  };
}

function AllocationBar({ allocation }: { allocation: AllocationBreakdown }) {
  const segments = [
    { key: 'stocks', label: 'Stocks', pct: allocation.stocksPct, color: 'bg-blue-500' },
    { key: 'bonds', label: 'Bonds', pct: allocation.bondsPct, color: 'bg-emerald-500' },
    { key: 'cash', label: 'Cash', pct: allocation.cashPct, color: 'bg-amber-400' },
    { key: 'other', label: 'Other', pct: allocation.otherPct, color: 'bg-purple-500' },
  ].filter(s => s.pct > 0);

  if (segments.length === 0) return null;

  return (
    <div className="w-full space-y-1">
      {/* Bar */}
      <div className="h-2 rounded-full overflow-hidden flex bg-muted">
        {segments.map((seg, idx) => (
          <div
            key={seg.key}
            className={cn(seg.color, idx === 0 && 'rounded-l-full', idx === segments.length - 1 && 'rounded-r-full')}
            style={{ width: `${seg.pct}%` }}
          />
        ))}
      </div>
      {/* Legend */}
      <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-xs text-muted-foreground">
        {segments.map(seg => (
          <div key={seg.key} className="flex items-center gap-1">
            <div className={cn('w-2 h-2 rounded-sm', seg.color)} />
            <span>{seg.label}</span>
            <span className="font-mono">{seg.pct.toFixed(0)}%</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function formatCurrency(value: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(value);
}

// Strip redundant account type prefixes/suffixes from holding names
function cleanHoldingName(name: string | undefined): string {
  if (!name) return '';
  return name
    .replace(/^\s*\[(?:Brokerage|Trad IRA|Roth IRA|Traditional IRA|IRA)\]\s*/i, '')
    .replace(/\s*\((?:Traditional IRA|Roth IRA|Brokerage|Taxable|Tax-Advantaged|IRA)\)\s*$/i, '')
    .trim();
}

export const HoldingsTable = forwardRef<HTMLDivElement, HoldingsTableProps>(function HoldingsTable({ holdings, sampleAccounts, onNavigateToAccounts }, ref) {
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({});

  // Group holdings by accounts
  const accountGroups = useMemo(() => {
    if (sampleAccounts && sampleAccounts.length > 0) {
      return groupHoldingsBySampleAccounts(holdings, sampleAccounts);
    }
    return groupHoldingsByInference(holdings);
  }, [holdings, sampleAccounts]);

  // Initialize expanded sections when account groups change - all collapsed by default
  useEffect(() => {
    const newExpanded: Record<string, boolean> = {};

    accountGroups.forEach((group) => {
      // Start with all sections collapsed
      newExpanded[group.id] = false;
    });

    setExpandedSections(newExpanded);
  }, [accountGroups.length]); // Only re-run when number of groups changes

  const toggleSection = (groupId: string) => {
    setExpandedSections(prev => ({
      ...prev,
      [groupId]: !prev[groupId],
    }));
  };

  const totalValue = holdings.reduce((sum, h) => sum + h.shares * h.currentPrice, 0);

  const renderHoldingRow = (holding: Holding) => {
    const value = holding.shares * holding.currentPrice;
    const gainLoss = value - (holding.shares * holding.costBasis);

    return (
      <TableRow key={holding.id} className="group">
        <TableCell className="sticky left-0 z-10 bg-background">
          <span className="font-mono text-sm font-medium uppercase">
            {holding.ticker}
          </span>
        </TableCell>
        <TableCell>
          <span className="text-sm">
            {cleanHoldingName(holding.name)}
          </span>
        </TableCell>
        <TableCell className="text-right">
          <span className="font-mono text-sm">
            {holding.shares.toLocaleString(undefined, { maximumFractionDigits: 4 })}
          </span>
        </TableCell>
        <TableCell className="text-right">
          <span className="font-mono text-sm">
            ${holding.currentPrice.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </span>
        </TableCell>
        <TableCell className="text-right">
          <span className="font-mono text-sm">
            ${holding.costBasis.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </span>
        </TableCell>
        <TableCell>
          <span className="text-sm text-muted-foreground">
            {holding.assetClass}
          </span>
        </TableCell>
        <TableCell className="text-right">
          <div className="font-mono font-medium">
            ${value.toLocaleString(undefined, { maximumFractionDigits: 0 })}
          </div>
          <div className={cn(
            'text-xs font-mono',
            gainLoss >= 0 ? 'value-positive' : 'value-negative'
          )}>
            {gainLoss >= 0 ? '+' : ''}{((gainLoss / (holding.shares * holding.costBasis || 1)) * 100).toFixed(1)}%
          </div>
        </TableCell>
      </TableRow>
    );
  };

  return (
    <div ref={ref} className="space-y-4">
      {/* Grand Total Summary */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 p-3 rounded-lg bg-muted/50 border">
        <div>
          <div className="text-sm text-muted-foreground">Grand Total</div>
          <div className="text-xl font-bold font-mono">{formatCurrency(totalValue)}</div>
        </div>
        <div className="text-sm text-muted-foreground">
          {holdings.length} holdings across {accountGroups.length} account{accountGroups.length !== 1 ? 's' : ''}
        </div>
      </div>

      <div className="flex items-center justify-end">
        <Button
          size="sm"
          onClick={() => onNavigateToAccounts ? onNavigateToAccounts() : toast.info('Navigate to Accounts to update holdings')}
          aria-label="Update holdings"
        >
          <RefreshCw size={14} className="mr-1.5" aria-hidden="true" />
          Update Holdings
        </Button>
      </div>

      {/* Holdings by Account Section */}
      {holdings.length === 0 ? (
        <EmptyHoldings
          onUpdateHoldings={onNavigateToAccounts}
          className="py-8 border border-border rounded-lg"
        />
      ) : (
        <div className="space-y-4">
          {accountGroups.map((group, groupIndex) => {
            const percentOfTotal = totalValue > 0 ? (group.totalValue / totalValue) * 100 : 0;
            const isExpanded = expandedSections[group.id] ?? true;
            const allocation = computeAllocation(group.holdings);

            return (
              <Collapsible
                key={group.id}
                open={isExpanded}
                onOpenChange={() => toggleSection(group.id)}
              >
                {/* Account Section Header - Clickable */}
                <CollapsibleTrigger asChild>
                  <div
                    className="w-full p-3 rounded-lg bg-muted/30 border hover:bg-muted/50 transition-colors cursor-pointer space-y-2"
                  >
                    {/* Top row: account info */}
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                      <div className="flex items-center gap-2">
                        <ChevronDown
                          size={18}
                          className={cn(
                            "text-muted-foreground transition-transform duration-200 shrink-0",
                            isExpanded ? "rotate-0" : "-rotate-90"
                          )}
                        />
                        <h3 className="font-semibold text-foreground">{group.label}</h3>
                        <Badge variant="secondary" className="text-xs">
                          {group.taxLabel}
                        </Badge>
                        {group.institution && (
                          <span className="text-xs text-muted-foreground">
                            {group.institution}
                            {group.accountMask && ` ••••${group.accountMask}`}
                          </span>
                        )}
                        <span className="text-xs text-muted-foreground">
                          ({group.holdings.length} holding{group.holdings.length !== 1 ? 's' : ''})
                        </span>
                      </div>
                      <div className="flex items-center gap-4 text-sm pl-6 sm:pl-0">
                        <span className="font-mono font-medium">{formatCurrency(group.totalValue)}</span>
                        <span className="text-muted-foreground">
                          {percentOfTotal.toFixed(1)}%
                        </span>
                      </div>
                    </div>
                    {/* Allocation bar */}
                    <div className="pl-6">
                      <AllocationBar allocation={allocation} />
                    </div>
                  </div>
                </CollapsibleTrigger>

                {/* Account Holdings Table */}
                <CollapsibleContent className="pt-2">
                  <div className="rounded-lg border border-border overflow-x-auto">
                    <Table style={{ minWidth: '900px' }}>
                      <TableHeader>
                        <TableRow className="bg-muted/50 hover:bg-muted/50">
                          <TableHead className="sticky left-0 z-10 bg-muted" style={{ width: '100px', minWidth: '100px' }}>Ticker</TableHead>
                          <TableHead style={{ width: '200px', minWidth: '200px' }}>Name</TableHead>
                          <TableHead style={{ width: '100px', minWidth: '100px' }} className="text-right">Shares</TableHead>
                          <TableHead style={{ width: '100px', minWidth: '100px' }} className="text-right">Price</TableHead>
                          <TableHead style={{ width: '100px', minWidth: '100px' }} className="text-right">Cost</TableHead>
                          <TableHead style={{ width: '130px', minWidth: '130px' }}>Class</TableHead>
                          <TableHead style={{ width: '120px', minWidth: '120px' }} className="text-right">Value</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {group.holdings.map(renderHoldingRow)}
                      </TableBody>
                    </Table>
                  </div>
                </CollapsibleContent>

                {/* Divider between sections (except last) */}
                {groupIndex < accountGroups.length - 1 && (
                  <div className="pt-2" />
                )}
              </Collapsible>
            );
          })}
        </div>
      )}
    </div>
  );
});
